import { useEffect } from "react";
import { FlaskConical, Plus, Scissors, UserRound, X } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { cn } from "@/shared/lib/cn";
import type { HospitalActCareType, HospitalActDetail } from "../types";

type HospitalActsModalProps = {
  hospitalName: string;
  acts: HospitalActDetail[];
  onClose: () => void;
};

const careTypeStyles: Record<
  HospitalActCareType,
  { className: string; icon: typeof Plus }
> = {
  nursing: { className: "bg-teal-soft text-teal", icon: Plus },
  consultation: { className: "bg-card-muted text-muted-foreground", icon: UserRound },
  surgery: { className: "bg-yellow-soft text-yellow", icon: Scissors },
  imagingLab: { className: "bg-purple-soft text-purple", icon: FlaskConical },
};

export function HospitalActsModal({ hospitalName, acts, onClose }: HospitalActsModalProps) {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.hospitalActs.modal;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={labels.close}
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
      />

      <div className="card-shadow relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-foreground">
            {labels.title.replace("{hospital}", hospitalName)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card-muted hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto">
          <table className="safecheck-table w-full min-w-[760px]">
            <thead className="sticky top-0 bg-card-muted/80 backdrop-blur-sm">
              <tr className="border-b border-border">
                {Object.values(labels.columns).map((header) => (
                  <th
                    key={header}
                    className={cn(
                      "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted",
                      header === labels.columns.cost && "text-right",
                    )}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {acts.map((act) => {
                const careStyle = careTypeStyles[act.careType];
                const CareIcon = careStyle.icon;

                return (
                  <tr key={act.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                    <td className="px-4 py-3 text-sm font-semibold text-pink">{act.code}</td>
                    <td className="px-4 py-3 text-sm font-medium uppercase text-foreground">{act.label}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex max-w-[220px] items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-semibold uppercase leading-tight",
                          careStyle.className,
                        )}
                      >
                        <CareIcon size={12} className="shrink-0" />
                        {labels.careTypes[act.careType]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-foreground">{act.costUsd}</td>
                    <td className="px-4 py-3 text-center text-sm text-foreground">{act.delayDays}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-card-muted px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-border hover:text-foreground"
          >
            {labels.close}
          </button>
        </div>
      </div>
    </div>
  );
}
